import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { OtCategoryFields, OtCategoryDisplay, otCategoryPayload, OT_CATEGORIES } from "@/components/OtCategoryFields";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CalendarCheck, Check, X, PencilSimple, Trash, Plus, ClipboardText, DownloadSimple } from "@phosphor-icons/react";

const OT_TYPES = ["Regular Overtime", "Restday Duty", "Legal Holiday", "Special Holiday"];

const STATUS_BADGE = {
  pending: "bg-warning/10 text-warning border-warning/20",
  approved: "bg-success/10 text-success border-success/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
};

function toCsv(headers, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
}
function downloadFile(name, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

function RowsTable({ rows }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50 hover:bg-muted/50">
            <TableHead>Date</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Reason (Work to be Done)</TableHead>
            <TableHead>Actual time</TableHead>
            <TableHead>Total Hrs</TableHead>
            <TableHead>Manager/Supervisor</TableHead>
            <TableHead>Remarks</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(rows || []).map((r, i) => (
            <TableRow key={i}>
              <TableCell className="font-medium">{r.date}</TableCell>
              <TableCell>{r.ot_type || "—"}</TableCell>
              <TableCell><OtCategoryDisplay row={r} /></TableCell>
              <TableCell>{r.reason}</TableCell>
              <TableCell className="text-muted-foreground">{r.time_from || "—"} – {r.time_to || "—"}</TableCell>
              <TableCell>{r.total_hours}</TableCell>
              <TableCell>{r.approver_name || "—"}</TableCell>
              <TableCell className="text-muted-foreground">{r.remarks || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function FilingCard({ o, canApprove, isAdmin, onDecide, onEdit, onDelete }) {
  return (
    <Card className="p-5 border-border shadow-none space-y-4 animate-fade-up" data-testid={`ot-filing-${o.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-bold text-lg">{o.employee_name}</h3>
            <Badge variant="outline" className={`capitalize font-medium ${STATUS_BADGE[o.status]}`}>{o.status}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {o.position || "—"} · {o.department || "—"} · Filed {o.date_filed} · Cut off: {o.cut_off || "—"} · <span className="font-medium text-foreground">{o.total_hours} hrs</span>
          </p>
          {o.decided_by_name && (
            <p className="text-xs text-muted-foreground mt-1">
              {o.status === "approved" ? "Approved" : "Rejected"} by {o.decided_by_name}
              {o.decision_note ? ` — ${o.decision_note}` : ""}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canApprove && o.status === "pending" && (
            <>
              <Button size="sm" onClick={() => onDecide(o, "approved")} data-testid={`ot-approve-${o.id}`} className="gap-1.5 bg-success hover:bg-success/90 text-white font-semibold">
                <Check className="h-3.5 w-3.5" /> Approve
              </Button>
              <Button size="sm" variant="outline" onClick={() => onDecide(o, "rejected")} data-testid={`ot-reject-${o.id}`} className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10">
                <X className="h-3.5 w-3.5" /> Reject
              </Button>
            </>
          )}
          {isAdmin && (
            <>
              <Button size="sm" variant="outline" onClick={() => onEdit(o)} data-testid={`ot-edit-${o.id}`} className="gap-1.5">
                <PencilSimple className="h-3.5 w-3.5" /> Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => onDelete(o)} data-testid={`ot-delete-${o.id}`} className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10">
                <Trash className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>
      <RowsTable rows={o.rows} />
    </Card>
  );
}

export default function OvertimeRecord() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canApprove = user?.role === "admin" || user?.role === "manager";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [approvers, setApprovers] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [accFilter, setAccFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/overtime");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/overtime/approvers").then(({ data }) => setApprovers(data)).catch(() => {});
  }, [load]);

  const decide = async (o, status) => {
    try {
      await api.put(`/overtime/${o.id}/decision`, { status });
      toast.success(`Overtime ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openEdit = (fo) => {
    const o = items.find((x) => x.id === fo.id) || fo;
    setEditing(o);
    setForm({ department: o.department || "", position: o.position || "", cut_off: o.cut_off || "",
      rows: (o.rows || []).map((r) => ({ ...r, category: r.category || "None", total_hours: r.total_hours ?? "" })) });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/overtime/${editing.id}`, {
        department: form.department, position: form.position, cut_off: form.cut_off,
        rows: form.rows.map((r) => ({
          date: r.date, ot_type: r.ot_type, ...otCategoryPayload(r), reason: r.reason, time_from: r.time_from, time_to: r.time_to,
          total_hours: parseFloat(r.total_hours) || 0, approver_id: r.approver_id, remarks: r.remarks,
        })),
      });
      toast.success("Overtime record updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/overtime/${confirmDel.id}`);
      toast.success("Overtime record deleted");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const editRow = (i, patch) => setForm((f) => ({ ...f, rows: f.rows.map((r, idx) => idx === i ? { ...r, ...patch } : r) }));

  const accounts = [...new Set(items.flatMap((o) => (o.rows || []).map((r) => (r.account_name || "").trim()).filter(Boolean)))].sort();
  const rowMatch = (r) => {
    const cat = r.category || "None";
    if (catFilter !== "all" && cat !== catFilter) return false;
    if (accFilter !== "all" && (r.account_name || "").trim() !== accFilter) return false;
    return true;
  };
  const filtering = catFilter !== "all" || accFilter !== "all";
  const visible = filtering
    ? items.map((o) => ({ ...o, rows: (o.rows || []).filter(rowMatch) })).filter((o) => o.rows.length)
    : items;
  const pending = visible.filter((o) => o.status === "pending");
  const decided = visible.filter((o) => o.status !== "pending");

  const downloadCsv = () => {
    if (!visible.length) { toast.error("No records to download"); return; }
    const headers = ["Employee", "Department", "Position", "Date Filed", "Cut Off", "Status",
      "Date", "Type", "Category", "Direct Call", "Account/Company Name", "Contact Person", "Reason", "From", "To", "Total Hrs", "Manager/Supervisor", "Remarks"];
    const out = [];
    visible.forEach((o) => (o.rows || []).forEach((r) => out.push([
      o.employee_name, o.department, o.position, o.date_filed, o.cut_off, o.status,
      r.date, r.ot_type, r.category, r.direct_call, r.account_name, r.contact_person, r.reason, r.time_from, r.time_to, r.total_hours, r.approver_name, r.remarks])));
    downloadFile(`overtime-records-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, out));
  };

  return (
    <div className="space-y-6" data-testid="overtime-record-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center shrink-0">
            <CalendarCheck weight="duotone" className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
            <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground">Schedules</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Overtime Record</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              {canApprove ? "Review and approve filed overtime." : "View filed and approved overtime."}
              {isAdmin ? " As admin you can edit records for checking." : ""}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={downloadCsv} data-testid="ot-download-csv" className="gap-1.5">
          <DownloadSimple className="h-4 w-4" /> Download CSV
        </Button>
      </div>

      <Card className="p-4 border-border shadow-none flex flex-wrap items-end gap-3" data-testid="ot-filters">
        <div className="space-y-1.5 w-full sm:w-56">
          <Label>Category</Label>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger data-testid="ot-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {OT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 w-full sm:w-64">
          <Label>Account / Company</Label>
          <Select value={accFilter} onValueChange={setAccFilter}>
            <SelectTrigger data-testid="ot-filter-account"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All accounts</SelectItem>
              {accounts.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {filtering && (
          <Button variant="ghost" onClick={() => { setCatFilter("all"); setAccFilter("all"); }} data-testid="ot-filter-clear">Clear filters</Button>
        )}
        <p className="text-sm text-muted-foreground sm:ml-auto" data-testid="ot-filter-count">{visible.length} filing{visible.length === 1 ? "" : "s"} shown</p>
      </Card>

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : (
        <>
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <ClipboardText weight="duotone" className="h-5 w-5 text-warning" />
              <h2 className="font-heading text-lg font-bold">Pending approval</h2>
              <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 font-semibold" data-testid="ot-pending-count">{pending.length}</Badge>
            </div>
            {pending.length === 0 ? (
              <p className="text-sm text-muted-foreground">No overtime awaiting approval.</p>
            ) : pending.map((o) => (
              <FilingCard key={o.id} o={o} canApprove={canApprove} isAdmin={isAdmin} onDecide={decide} onEdit={openEdit} onDelete={setConfirmDel} />
            ))}
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-lg font-bold">Records</h2>
            {decided.length === 0 ? (
              <p className="text-sm text-muted-foreground">No overtime records yet.</p>
            ) : decided.map((o) => (
              <FilingCard key={o.id} o={o} canApprove={canApprove} isAdmin={isAdmin} onDecide={decide} onEdit={openEdit} onDelete={setConfirmDel} />
            ))}
          </section>
        </>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="ot-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit overtime record</DialogTitle>
            <DialogDescription>{editing?.employee_name}</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="ot-edit-department" /></div>
                <div className="space-y-1.5"><Label>Position</Label><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="ot-edit-position" /></div>
                <div className="space-y-1.5"><Label>Cut off</Label><Input value={form.cut_off} onChange={(e) => setForm({ ...form, cut_off: e.target.value })} data-testid="ot-edit-cutoff" /></div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Category</TableHead><TableHead>Reason</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Hrs</TableHead><TableHead>Remarks</TableHead><TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.rows.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell><Input type="date" value={r.date} onChange={(e) => editRow(i, { date: e.target.value })} /></TableCell>
                        <TableCell>
                          <Select value={r.ot_type} onValueChange={(v) => editRow(i, { ot_type: v })}>
                            <SelectTrigger className="min-w-[150px]"><SelectValue placeholder="Type" /></SelectTrigger>
                            <SelectContent>
                              {OT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="align-top"><OtCategoryFields row={r} onChange={(p) => editRow(i, p)} testPrefix={`ot-edit-row-${i}`} /></TableCell>
                        <TableCell><Input value={r.reason} onChange={(e) => editRow(i, { reason: e.target.value })} /></TableCell>
                        <TableCell><Input type="time" value={r.time_from} onChange={(e) => editRow(i, { time_from: e.target.value })} /></TableCell>
                        <TableCell><Input type="time" value={r.time_to} onChange={(e) => editRow(i, { time_to: e.target.value })} /></TableCell>
                        <TableCell><Input type="number" step="0.25" className="w-20" value={r.total_hours} onChange={(e) => editRow(i, { total_hours: e.target.value })} /></TableCell>
                        <TableCell><Input value={r.remarks} onChange={(e) => editRow(i, { remarks: e.target.value })} /></TableCell>
                        <TableCell><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setForm((f) => ({ ...f, rows: f.rows.filter((_, idx) => idx !== i) }))}><Trash className="h-4 w-4" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setForm((f) => ({ ...f, rows: [...f.rows, { date: "", category: "None", reason: "", time_from: "", time_to: "", total_hours: "", approver_id: form.rows[0]?.approver_id || "", remarks: "" }] }))}>
                <Plus className="h-4 w-4" /> Add row
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="ot-edit-save" className="font-semibold">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent data-testid="ot-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-destructive">Delete overtime record?</DialogTitle>
            <DialogDescription>This permanently removes {confirmDel?.employee_name}'s overtime filing. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="ot-delete-confirm" className="gap-1.5"><Trash className="h-4 w-4" /> Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
